import React from 'react';

import { Col as AntCol, Row as AntRow } from 'antd';
import { Column, Row, Space, Text } from 'farmhub-ui-core';
import moment from 'moment';

import { useAppSelector } from 'app';
import { FlexAvatar } from 'components/Commons';
import { env } from 'env';
import { selectAppState, selectProductUnit } from 'slice';
import {
  IMAGETYPE,
  buildEntityDomain,
  formatAppearPhoneNumber,
  formatImageUrl,
  formatMoney,
  formatQuantity,
  genWholesaleUnit,
  getBase64Image,
  getFullAddress,
  getOrderItemTotalPrice,
  isWholesaleOrder,
} from 'utils';

import {
  defaultSettingOrderInfoVisible,
  defaultSettingQuantityVisible,
} from '../commons/data';
import K80BillSkeleton from '../components/K80BillSkeleton';
import { CQRCode, Hr, RowInfo, RowTableInfo, Wrapper } from './styles';

interface K80BillContentProps {
  bill: any;
  isCart?: boolean;
  isLoading?: boolean;
  settingQuantityVisible?: any;
  settingOrderInfoVisible?: any;
}

const K80BillContent = ({
  bill,
  isCart = false,
  isLoading = false,
  settingQuantityVisible = defaultSettingQuantityVisible,
  settingOrderInfoVisible = defaultSettingOrderInfoVisible,
}: K80BillContentProps): JSX.Element => {
  const productUnit = useAppSelector(selectProductUnit);
  const app = useAppSelector(selectAppState);
  const entityID = useAppSelector(state => state.account.item?.id);

  const handleLoadImage = () => {
    setTimeout(() => {
      const imageSource: HTMLImageElement | null = document.getElementById(
        `imageSource-${bill.code}`
      ) as HTMLImageElement;

      const imageResult: HTMLImageElement | null = document.getElementById(
        `imageResult-${bill.code}`
      ) as HTMLImageElement;

      if (imageSource && imageResult) {
        imageResult.src = getBase64Image(imageSource);
      }
    }, 1);
  };

  const renderAvatar = React.useMemo(
    () =>
      settingOrderInfoVisible.isAvatar
        ? bill.supplier?.avatar
        : bill.supplier?.avatarText,
    [bill, settingOrderInfoVisible]
  );

  return (
    <Wrapper className="bill-content">
      {isLoading ? (
        <K80BillSkeleton />
      ) : (
        <>
          {renderAvatar && settingOrderInfoVisible.isVisibleLogo && (
            <Column
              alignItems="center"
              styles={{ flexShrink: 0 }}
              width="fit-content"
            >
              <>
                <img
                  alt="avatar"
                  crossOrigin="anonymous"
                  id={`imageSource-${bill.code}`}
                  src={formatImageUrl(
                    String(renderAvatar),
                    IMAGETYPE.AVATAR.xs,
                    true
                  )}
                  style={{
                    display: 'none',
                    maxHeight: 84,
                    maxWidth: 168,
                  }}
                  onLoad={handleLoadImage}
                />
                <FlexAvatar
                  alt="avatar"
                  crossOrigin="anonymous"
                  id={`imageResult-${bill.code}`}
                  maxHeight="84px"
                  maxWidth="168px"
                  src={formatImageUrl(
                    String(renderAvatar),
                    IMAGETYPE.AVATAR.xxs,
                    true
                  )}
                />
                <Space height={6} />
              </>
            </Column>
          )}
          <Text color="black" weight="bold">
            {bill?.supplier?.displayName}
          </Text>
          <Text
            color="black"
            lineHeight="22px"
            size="tiny"
            styles={{ 'text-align': 'center' }}
            weight="regular"
          >
            {buildEntityDomain({
              entityType: bill?.supplier?.type,
              entitySlug: bill?.supplier?.slug,
              domain: bill?.supplier?.domain,
              domainStatus: bill?.supplier?.domainStatus,
              website: bill?.supplier?.website,
            })}
          </Text>
          <Text
            color="black"
            lineHeight="22px"
            size="tiny"
            styles={{ 'text-align': 'center' }}
            weight="regular"
          >
            {bill?.supplier &&
              getFullAddress(
                bill?.supplier?.address,
                bill?.supplier?.ward?.district?.fullName,
                bill?.supplier?.ward?.province?.fullName,
                bill?.supplier?.ward?.fullName
              )}
          </Text>
          <Hr type="dashed" />
          <Text color="black" lineHeight="20px" weight="bold">
            HÓA ĐƠN BÁN HÀNG
          </Text>
          <Text
            color="black"
            lineHeight="20px"
            styles={{ 'text-align': 'center' }}
            weight="regular"
          >
            {moment().format('DD/MM/YYYY HH:mm')}
          </Text>
          <Space height={6} />
          {!isCart && settingOrderInfoVisible.isVisibleQRCode && (
            <Column alignItems="center" width="160px">
              <CQRCode
                level="L"
                renderAs="svg"
                value={`${app?.domains?.hubBilling?.domain ??
                  env.service.hubbillingWebApp
                  }/${bill?.code}`}
              />
              <Space height={4} />
              <Text
                color="black"
                lineHeight="22px"
                lineNumber={2}
                styles={{ 'text-align': 'center' }}
                weight="regular"
              >
                Mã thông tin hóa đơn
              </Text>
            </Column>
          )}

          <Space height={12} />
          {!isCart && settingOrderInfoVisible.isVisibleOrderCode && (
            <AntRow style={{ width: '100%' }}>
              <AntCol flex="80px">
                <Text color="black" lineHeight="22px" weight="regular">
                  Mã ĐH:
                </Text>
              </AntCol>
              <AntCol flex="calc(100% - 80px)">
                <Text color="black" lineHeight="22px" weight="regular">
                  #{bill?.code}
                </Text>
              </AntCol>
            </AntRow>
          )}

          {settingOrderInfoVisible.isVisibleCustomer && (
            <AntRow style={{ width: '100%' }}>
              <AntCol flex="80px">
                <Text color="black" lineHeight="22px" weight="regular">
                  K.Hàng:
                </Text>
              </AntCol>
              <AntCol flex="calc(100% - 80px)">
                <Text
                  className="info"
                  color="black"
                  lineHeight="22px"
                  weight="regular"
                >
                  {bill?.customer?.displayName}
                </Text>
              </AntCol>
            </AntRow>
          )}

          {settingOrderInfoVisible.isVisibleCustomerName && (
            <AntRow style={{ width: '100%' }}>
              <AntCol flex="80px">
                <Text color="black" lineHeight="22px" weight="regular">
                  Họ và tên:
                </Text>
              </AntCol>
              <AntCol flex="calc(100% - 80px)">
                <Text
                  className="info"
                  color="black"
                  lineHeight="22px"
                  weight="regular"
                >
                  {bill?.additionalInfo?.address?.title}
                </Text>
              </AntCol>
            </AntRow>
          )}

          {settingOrderInfoVisible.isVisibleCustomerPhoneNumber && (
            <AntRow style={{ width: '100%' }}>
              <AntCol flex="80px">
                <Text color="black" lineHeight="22px" weight="regular">
                  SĐT:
                </Text>
              </AntCol>
              <AntCol flex="calc(100% - 80px)">
                <Text
                  className="info"
                  color="black"
                  lineHeight="22px"
                  weight="regular"
                >
                  {formatAppearPhoneNumber(
                    bill?.additionalInfo?.address?.phoneNumber
                  )}
                </Text>
              </AntCol>
            </AntRow>
          )}

          {settingOrderInfoVisible.isVisibleCustomerAddress && (
            <AntRow style={{ width: '100%' }}>
              <AntCol flex="80px">
                <Text color="black" lineHeight="22px" weight="regular">
                  Địa chỉ:
                </Text>
              </AntCol>
              <AntCol flex="calc(100% - 80px)">
                <Text
                  className="info"
                  color="black"
                  lineHeight="22px"
                  weight="regular"
                >
                  {getFullAddress(
                    bill?.additionalInfo?.address?.address,
                    bill?.additionalInfo?.address?.district?.fullName,
                    bill?.additionalInfo?.address?.province?.fullName,
                    bill?.additionalInfo?.address?.ward?.fullName
                  )}
                </Text>
              </AntCol>
            </AntRow>
          )}

          {settingOrderInfoVisible.isVisibleTotalProducts && (
            <AntRow style={{ width: '100%' }}>
              <AntCol flex="80px">
                <Text color="black" lineHeight="22px" weight="regular">
                  Tổng SP:
                </Text>
              </AntCol>
              <AntCol flex="calc(100% - 80px)">
                <Text
                  className="info"
                  color="black"
                  lineHeight="22px"
                  weight="regular"
                >
                  {formatQuantity(bill.orderItems.length)}
                </Text>
              </AntCol>
            </AntRow>
          )}

          {(settingOrderInfoVisible.isVisibleQRCode ||
            settingOrderInfoVisible.isVisibleOrderCode ||
            settingOrderInfoVisible.isVisibleCustomer ||
            settingOrderInfoVisible.isVisibleCustomerName ||
            settingOrderInfoVisible.isVisibleCustomerPhoneNumber ||
            settingOrderInfoVisible.isVisibleCustomerAddress) && (
              <Space height={8} />
            )}

          {settingOrderInfoVisible.isVisibleProduct && (
            <>
              <Hr type="solid" />
              <RowTableInfo>
                <Text
                  color="black"
                  lineHeight="22px"
                  style={{
                    width: settingOrderInfoVisible.isVisibleProductPrice
                      ? '20%'
                      : '100%',
                  }}
                >
                  S.Phẩm
                </Text>

                {settingQuantityVisible.isVisibleCustomerQuantity && (
                  <Text
                    className="order"
                    color="black"
                    lineHeight="22px"
                    style={{
                      width: '14%',
                      flexShrink: 0,
                      textAlign: 'center',
                    }}
                  >
                    Đặt
                  </Text>
                )}

                {settingQuantityVisible.isVisibleSupplierQuantity && (
                  <Text
                    className="order"
                    color="black"
                    lineHeight="22px"
                    style={{
                      width: '14%',
                      flexShrink: 0,
                      textAlign: 'center',
                    }}
                  >
                    Chốt
                  </Text>
                )}

                {settingQuantityVisible.isVisibleSupplierDelivered && (
                  <Text
                    className="send"
                    color="black"
                    lineHeight="22px"
                    style={{
                      width: '14%',
                      flexShrink: 0,
                      textAlign: 'center',
                    }}
                  >
                    Giao
                  </Text>
                )}

                {settingQuantityVisible.isVisibleCustomerReceived && (
                  <Text
                    className="send"
                    color="black"
                    lineHeight="22px"
                    style={{
                      width: '14%',
                      flexShrink: 0,
                      textAlign: 'center',
                    }}
                  >
                    Nhận
                  </Text>
                )}

                {settingOrderInfoVisible.isVisibleProductPrice && (
                  <Text
                    color="black"
                    lineHeight="22px"
                    style={{
                      width: '24%',
                      flexShrink: 0,
                      textAlign: 'end',
                    }}
                  >
                    T.Tiền
                  </Text>
                )}
              </RowTableInfo>
              <Hr type="solid" />

              {settingOrderInfoVisible.isVisibleProductCategory &&
                (bill?.entitySubCategories || []).map(subCategory => (
                  <>
                    <Row justifyContent="flex-start">
                      <Text
                        color="black"
                        lineHeight="22px"
                        styles={{ 'text-transform': 'uppercase' }}
                        weight="extraBold"
                      >
                        {`${subCategory.name} (${subCategory.orderItems.length})`}
                      </Text>
                    </Row>
                    <Hr type="solid" />
                    {subCategory.orderItems.map(orderItem => (
                      <>
                        <Row
                          alignItems="flex-start"
                          justifyContent="space-between"
                        >
                          <Text
                            color="black"
                            lineHeight="22px"
                            styles={{
                              'text-transform': 'uppercase',
                              width: '100%',
                            }}
                            weight="regular"
                          >
                            {orderItem.product && orderItem.product?.name}
                            {orderItem.supplierProduct &&
                              bill?.customer?.id === entityID
                              ? orderItem.customerProduct?.name
                              : orderItem.supplierProduct?.name}{' '}
                            <Text
                              color="black"
                              lineHeight="22px"
                              styles={{
                                'text-transform': 'uppercase',
                                display: 'inline-block',
                              }}
                              weight="regular"
                            >
                              {' '}
                              {settingOrderInfoVisible.isVisibleProductUnit &&
                                ` (${!isWholesaleOrder(bill?.type)
                                  ? genWholesaleUnit({
                                    unitPrefix:
                                      productUnit.prefixObj[
                                      orderItem.supplierProductRetailUnit
                                        ?.prefix
                                      ],
                                    unitWeight:
                                      orderItem.supplierProductRetailUnit
                                        ?.weight,
                                    unitPostfix:
                                      productUnit.postfixObj[
                                      orderItem.supplierProductRetailUnit
                                        ?.postfix
                                      ],
                                  })
                                  : genWholesaleUnit({
                                    unitPrefix:
                                      productUnit.prefixObj[
                                      orderItem
                                        .customerProductWholesaleUnit
                                        ?.prefix
                                        ? orderItem
                                          .customerProductWholesaleUnit
                                          ?.prefix
                                        : orderItem
                                          .supplierProductWholesaleUnit
                                          ?.prefix
                                      ],
                                    unitWeight:
                                      orderItem.customerProductWholesaleUnit
                                        ?.weight != null
                                        ? orderItem
                                          .customerProductWholesaleUnit
                                          ?.weight
                                        : orderItem
                                          .supplierProductWholesaleUnit
                                          ?.weight,
                                    unitPostfix:
                                      productUnit.postfixObj[
                                      orderItem
                                        .customerProductWholesaleUnit
                                        ?.postfix
                                        ? orderItem
                                          .customerProductWholesaleUnit
                                          ?.postfix
                                        : orderItem
                                          .supplierProductWholesaleUnit
                                          ?.postfix
                                      ],
                                  })
                                })`}
                            </Text>
                          </Text>

                          {/* dat chot title */}
                          {settingQuantityVisible.isVisibleCustomerQuantity &&
                            !settingOrderInfoVisible.isVisibleProductPrice && (
                              <Text
                                className="order"
                                color="black"
                                lineHeight="22px"
                                size="xxs"
                                style={{
                                  textAlign: 'center',
                                  width: '14%',
                                  flexShrink: 0,
                                }}
                                weight="regular"
                              >
                                {orderItem.customerQuantity !== null
                                  ? orderItem.customerQuantity
                                  : '........'}
                              </Text>
                            )}
                          {settingQuantityVisible.isVisibleSupplierQuantity &&
                            !settingOrderInfoVisible.isVisibleProductPrice && (
                              <Text
                                className="order"
                                color="black"
                                lineHeight="22px"
                                size="xxs"
                                style={{
                                  textAlign: 'center',
                                  width: '14%',
                                  flexShrink: 0,
                                }}
                                weight="regular"
                              >
                                {orderItem.supplierQuantity !== null
                                  ? orderItem.supplierQuantity
                                  : '........'}
                              </Text>
                            )}

                          {/* giao nhan title */}
                          {settingQuantityVisible.isVisibleSupplierDelivered &&
                            !settingOrderInfoVisible.isVisibleProductPrice && (
                              <Text
                                className="order"
                                color="black"
                                lineHeight="22px"
                                size="xxs"
                                style={{
                                  textAlign: 'center',
                                  width: '14%',
                                  flexShrink: 0,
                                }}
                                weight="regular"
                              >
                                {orderItem.supplierDeliveredQuantity !== null
                                  ? orderItem.supplierDeliveredQuantity
                                  : '........'}
                              </Text>
                            )}
                          {settingQuantityVisible.isVisibleCustomerReceived &&
                            !settingOrderInfoVisible.isVisibleProductPrice && (
                              <Text
                                className="order"
                                color="black"
                                lineHeight="22px"
                                size="xxs"
                                style={{
                                  textAlign: 'center',
                                  width: '14%',
                                  flexShrink: 0,
                                }}
                                weight="regular"
                              >
                                {orderItem.customerReceivedQuantity !== null
                                  ? orderItem.customerReceivedQuantity
                                  : '........'}
                              </Text>
                            )}
                        </Row>
                        <RowTableInfo>
                          {settingOrderInfoVisible.isVisibleProductPrice && (
                            <Text
                              color="black"
                              lineHeight="22px"
                              size="xxs"
                              style={{ width: '20%' }}
                              weight="regular"
                            >
                              {orderItem.product &&
                                formatMoney(orderItem?.price)}
                              {orderItem.supplierProduct &&
                                (bill?.customer?.id === entityID
                                  ? formatMoney(
                                    orderItem.customerProduct?.price
                                  )
                                  : formatMoney(
                                    orderItem.supplierProduct?.price
                                  ))}
                              đ
                            </Text>
                          )}

                          {/* dat chot */}
                          {settingQuantityVisible.isVisibleCustomerQuantity &&
                            settingOrderInfoVisible.isVisibleProductPrice && (
                              <Text
                                className="order"
                                color="black"
                                lineHeight="22px"
                                size="xxs"
                                style={{
                                  textAlign: 'center',
                                  width: '14%',
                                  flexShrink: 0,
                                }}
                                weight="regular"
                              >
                                {orderItem.customerQuantity !== null
                                  ? orderItem.customerQuantity
                                  : '........'}
                              </Text>
                            )}
                          {settingQuantityVisible.isVisibleSupplierQuantity &&
                            settingOrderInfoVisible.isVisibleProductPrice && (
                              <Text
                                className="order"
                                color="black"
                                lineHeight="22px"
                                size="xxs"
                                style={{
                                  textAlign: 'center',
                                  width: '14%',
                                  flexShrink: 0,
                                }}
                                weight="regular"
                              >
                                {orderItem.supplierQuantity !== null
                                  ? orderItem.supplierQuantity
                                  : '........'}
                              </Text>
                            )}

                          {/* giao nhan */}
                          {settingQuantityVisible.isVisibleSupplierDelivered &&
                            settingOrderInfoVisible.isVisibleProductPrice && (
                              <Text
                                className="order"
                                color="black"
                                lineHeight="22px"
                                size="xxs"
                                style={{
                                  textAlign: 'center',
                                  width: '14%',
                                  flexShrink: 0,
                                }}
                                weight="regular"
                              >
                                {orderItem.supplierDeliveredQuantity !== null
                                  ? orderItem.supplierDeliveredQuantity
                                  : '........'}
                              </Text>
                            )}
                          {settingQuantityVisible.isVisibleCustomerReceived &&
                            settingOrderInfoVisible.isVisibleProductPrice && (
                              <Text
                                className="order"
                                color="black"
                                lineHeight="22px"
                                size="xxs"
                                style={{
                                  textAlign: 'center',
                                  width: '14%',
                                  flexShrink: 0,
                                }}
                                weight="regular"
                              >
                                {orderItem.customerReceivedQuantity !== null
                                  ? orderItem.customerReceivedQuantity
                                  : '........'}
                              </Text>
                            )}

                          {settingOrderInfoVisible.isVisibleProductPrice && (
                            <Text
                              color="black"
                              lineHeight="22px"
                              size="xxs"
                              style={{
                                width: '24%',
                                textAlign: 'end',
                                flexShrink: 0,
                              }}
                              weight="regular"
                            >
                              {orderItem.product &&
                                getOrderItemTotalPrice(
                                  orderItem,
                                  bill?.status,
                                  bill?.trackingStatus
                                )}
                              {orderItem.supplierProduct &&
                                formatMoney(orderItem.price)}
                              đ
                            </Text>
                          )}
                        </RowTableInfo>
                        {(settingOrderInfoVisible.isVisibleCustomerOrderItemNote ||
                          settingOrderInfoVisible.isVisibleSupplierOrderItemNote) && (
                            <Row>
                              <Text color="black" size="xxs" weight="bold">
                                Ghi chú:{' '}
                                <Text color="black" size="xxs">
                                  {[
                                    ...(settingOrderInfoVisible.isVisibleCustomerOrderItemNote
                                      ? [orderItem.customerNote || '']
                                      : []),
                                    ...(settingOrderInfoVisible.isVisibleSupplierOrderItemNote
                                      ? [orderItem.supplierNote || '']
                                      : []),
                                  ]
                                    .filter(item => item !== '')
                                    .join(', ')}
                                </Text>
                              </Text>
                            </Row>
                          )}
                        <Hr type="dashed" />
                      </>
                    ))}
                  </>
                ))}

              {!settingOrderInfoVisible.isVisibleProductCategory &&
                (bill.orderItems || []).map(orderItem => (
                  <>
                    <Row alignItems="flex-start" justifyContent="space-between">
                      <Text
                        color="black"
                        lineHeight="22px"
                        styles={{
                          'text-transform': 'uppercase',
                          width: '100%',
                        }}
                        weight="regular"
                      >
                        {orderItem.product && orderItem.product?.name}
                        {orderItem.supplierProduct &&
                          bill?.customer?.id === entityID
                          ? orderItem.customerProduct?.name
                          : orderItem.supplierProduct?.name}{' '}
                        <Text
                          color="black"
                          lineHeight="22px"
                          styles={{
                            'text-transform': 'uppercase',
                            display: 'inline-block',
                          }}
                          weight="regular"
                        >
                          {settingOrderInfoVisible.isVisibleProductUnit &&
                            ` (${!isWholesaleOrder(bill?.type)
                              ? genWholesaleUnit({
                                unitPrefix:
                                  productUnit.prefixObj[
                                  orderItem.supplierProductRetailUnit
                                    ?.prefix
                                  ],
                                unitWeight:
                                  orderItem.supplierProductRetailUnit
                                    ?.weight,
                                unitPostfix:
                                  productUnit.postfixObj[
                                  orderItem.supplierProductRetailUnit
                                    ?.postfix
                                  ],
                              })
                              : genWholesaleUnit({
                                unitPrefix:
                                  productUnit.prefixObj[
                                  orderItem.customerProductWholesaleUnit
                                    ?.prefix
                                    ? orderItem
                                      .customerProductWholesaleUnit
                                      ?.prefix
                                    : orderItem
                                      .supplierProductWholesaleUnit
                                      ?.prefix
                                  ],
                                unitWeight:
                                  orderItem.customerProductWholesaleUnit
                                    ?.weight != null
                                    ? orderItem.customerProductWholesaleUnit
                                      ?.weight
                                    : orderItem.supplierProductWholesaleUnit
                                      ?.weight,
                                unitPostfix:
                                  productUnit.postfixObj[
                                  orderItem.customerProductWholesaleUnit
                                    ?.postfix
                                    ? orderItem
                                      .customerProductWholesaleUnit
                                      ?.postfix
                                    : orderItem
                                      .supplierProductWholesaleUnit
                                      ?.postfix
                                  ],
                              })
                            })`}
                        </Text>
                      </Text>

                      {/* dat chot title */}
                      {settingQuantityVisible.isVisibleCustomerQuantity &&
                        !settingOrderInfoVisible.isVisibleProductPrice && (
                          <Text
                            className="order"
                            color="black"
                            lineHeight="22px"
                            size="xxs"
                            style={{
                              textAlign: 'center',
                              width: '14%',
                              flexShrink: 0,
                            }}
                            weight="regular"
                          >
                            {orderItem.customerQuantity !== null
                              ? orderItem.customerQuantity
                              : '........'}
                          </Text>
                        )}
                      {settingQuantityVisible.isVisibleSupplierQuantity &&
                        !settingOrderInfoVisible.isVisibleProductPrice && (
                          <Text
                            className="order"
                            color="black"
                            lineHeight="22px"
                            size="xxs"
                            style={{
                              textAlign: 'center',
                              width: '14%',
                              flexShrink: 0,
                            }}
                            weight="regular"
                          >
                            {orderItem.supplierQuantity !== null
                              ? orderItem.supplierQuantity
                              : '........'}
                          </Text>
                        )}

                      {/* giao nhan title */}
                      {settingQuantityVisible.isVisibleSupplierDelivered &&
                        !settingOrderInfoVisible.isVisibleProductPrice && (
                          <Text
                            className="order"
                            color="black"
                            lineHeight="22px"
                            size="xxs"
                            style={{
                              textAlign: 'center',
                              width: '14%',
                              flexShrink: 0,
                            }}
                            weight="regular"
                          >
                            {orderItem.supplierDeliveredQuantity !== null
                              ? orderItem.supplierDeliveredQuantity
                              : '........'}
                          </Text>
                        )}
                      {settingQuantityVisible.isVisibleCustomerReceived &&
                        !settingOrderInfoVisible.isVisibleProductPrice && (
                          <Text
                            className="order"
                            color="black"
                            lineHeight="22px"
                            size="xxs"
                            style={{
                              textAlign: 'center',
                              width: '14%',
                              flexShrink: 0,
                            }}
                            weight="regular"
                          >
                            {orderItem.customerReceivedQuantity !== null
                              ? orderItem.customerReceivedQuantity
                              : '........'}
                          </Text>
                        )}
                    </Row>
                    <RowTableInfo>
                      {settingOrderInfoVisible.isVisibleProductPrice && (
                        <Text
                          color="black"
                          lineHeight="22px"
                          size="xxs"
                          style={{ width: '20%' }}
                          weight="regular"
                        >
                          {orderItem.product && formatMoney(orderItem?.price)}
                          {orderItem.supplierProduct &&
                            (bill?.customer?.id === entityID
                              ? formatMoney(orderItem.customerProduct?.price)
                              : formatMoney(orderItem.supplierProduct?.price))}
                          đ
                        </Text>
                      )}

                      {/* dat chot */}
                      {settingQuantityVisible.isVisibleCustomerQuantity &&
                        settingOrderInfoVisible.isVisibleProductPrice && (
                          <Text
                            className="order"
                            color="black"
                            lineHeight="22px"
                            size="xxs"
                            style={{
                              textAlign: 'center',
                              width: '14%',
                              flexShrink: 0,
                            }}
                            weight="regular"
                          >
                            {orderItem.customerQuantity !== null
                              ? orderItem.customerQuantity
                              : '........'}
                          </Text>
                        )}
                      {settingQuantityVisible.isVisibleSupplierQuantity &&
                        settingOrderInfoVisible.isVisibleProductPrice && (
                          <Text
                            className="order"
                            color="black"
                            lineHeight="22px"
                            size="xxs"
                            style={{
                              textAlign: 'center',
                              width: '14%',
                              flexShrink: 0,
                            }}
                            weight="regular"
                          >
                            {orderItem.supplierQuantity !== null
                              ? orderItem.supplierQuantity
                              : '........'}
                          </Text>
                        )}

                      {/* giao nhan */}
                      {settingQuantityVisible.isVisibleSupplierDelivered &&
                        settingOrderInfoVisible.isVisibleProductPrice && (
                          <Text
                            className="order"
                            color="black"
                            lineHeight="22px"
                            size="xxs"
                            style={{
                              textAlign: 'center',
                              width: '14%',
                              flexShrink: 0,
                            }}
                            weight="regular"
                          >
                            {orderItem.supplierDeliveredQuantity !== null
                              ? orderItem.supplierDeliveredQuantity
                              : '........'}
                          </Text>
                        )}
                      {settingQuantityVisible.isVisibleCustomerReceived &&
                        settingOrderInfoVisible.isVisibleProductPrice && (
                          <Text
                            className="order"
                            color="black"
                            lineHeight="22px"
                            size="xxs"
                            style={{
                              textAlign: 'center',
                              width: '14%',
                              flexShrink: 0,
                            }}
                            weight="regular"
                          >
                            {orderItem.customerReceivedQuantity !== null
                              ? orderItem.customerReceivedQuantity
                              : '........'}
                          </Text>
                        )}

                      {settingOrderInfoVisible.isVisibleProductPrice && (
                        <Text
                          color="black"
                          lineHeight="22px"
                          size="xxs"
                          style={{
                            width: '24%',
                            textAlign: 'end',
                            flexShrink: 0,
                          }}
                          weight="regular"
                        >
                          {orderItem.product &&
                            getOrderItemTotalPrice(
                              orderItem,
                              bill?.status,
                              bill?.trackingStatus
                            )}
                          {/* case bill in cart modal */}
                          {orderItem.supplierProduct &&
                            formatMoney(orderItem.price)}
                          đ
                        </Text>
                      )}
                    </RowTableInfo>

                    {(settingOrderInfoVisible.isVisibleCustomerOrderItemNote ||
                      settingOrderInfoVisible.isVisibleSupplierOrderItemNote) && (
                        <Row>
                          <Text color="black" size="xxs" weight="bold">
                            Ghi chú:{' '}
                            <Text color="black" size="xxs">
                              {[
                                ...(settingOrderInfoVisible.isVisibleCustomerOrderItemNote
                                  ? [orderItem.customerNote || '']
                                  : []),
                                ...(settingOrderInfoVisible.isVisibleSupplierOrderItemNote
                                  ? [orderItem.supplierNote || '']
                                  : []),
                              ]
                                .filter(item => item !== '')
                                .join(', ')}
                            </Text>
                          </Text>
                        </Row>
                      )}
                    <Hr type="dashed" />
                  </>
                ))}
            </>
          )}

          {settingOrderInfoVisible.isVisibleProductPrice && (
            <>
              <RowInfo justify="flex-end" left={-1}>
                <Text color="black" lineHeight="22px" weight="bold">
                  {formatMoney(
                    Number(bill?.totalPrice) +
                    Number(bill?.totalAdjustment) -
                    Number(bill?.deliveryFee)
                  ) || '0'}
                  đ
                </Text>
                <Text
                  className="info"
                  color="black"
                  lineHeight="22px"
                  weight="bold"
                >
                  Tạm tính:
                </Text>
              </RowInfo>
              <RowInfo justify="flex-end" left={-1}>
                <Text color="black" lineHeight="22px" weight="bold">
                  {formatMoney(bill?.deliveryFee) || '0'}đ
                </Text>
                <Text
                  className="info"
                  color="black"
                  lineHeight="22px"
                  weight="bold"
                >
                  Phí giao hàng:
                </Text>
              </RowInfo>
              <RowInfo justify="flex-end" left={-1}>
                <Text color="black" lineHeight="22px" weight="bold">
                  {formatMoney(bill?.totalAdjustment) || '0'}đ
                </Text>
                <Text
                  className="info"
                  color="black"
                  lineHeight="22px"
                  weight="bold"
                >
                  Giảm giá:
                </Text>
              </RowInfo>
              <RowInfo justify="flex-end" left={-1}>
                <Text color="black" lineHeight="22px" weight="bold">
                  {formatMoney(bill?.totalPrice) || '0'}đ
                </Text>
                <Text
                  className="info"
                  color="black"
                  lineHeight="22px"
                  weight="bold"
                >
                  Tổng tiền:
                </Text>
              </RowInfo>
              <Hr type="dashed" />
            </>
          )}

          {(settingOrderInfoVisible.isVisibleCustomerOrderNote ||
            settingOrderInfoVisible.isVisibleSupplierOrderNote) && (
              <>
                <div style={{ display: 'inline', width: '100%', minHeight: 42 }}>
                  <Text color="black" lineHeight="18px" weight="bold">
                    Ghi chú:
                  </Text>
                  <Text
                    color="black"
                    lineHeight="18px"
                    styles={{ 'margin-left': '3px' }}
                  >
                    {[
                      ...(settingOrderInfoVisible.isVisibleCustomerOrderNote
                        ? [bill?.customerNote || '']
                        : []),
                      ...(settingOrderInfoVisible.isVisibleSupplierOrderNote
                        ? [bill?.supplierNote || '']
                        : []),
                    ]
                      .filter(item => item !== '')
                      .join(', ')}
                  </Text>
                </div>
                <Hr type="dashed" />
              </>
            )}
          <Space height={12} />
          <Text
            color="black"
            lineHeight="22px"
            size="tiny"
            styles={{ 'text-align': 'center', 'font-style': 'italic' }}
          >
            (Tổng đài góp ý/khiếu nại:{' '}
            {formatAppearPhoneNumber(bill?.supplier?.phoneNumber)} )
          </Text>
          <Text
            color="black"
            lineHeight="22px"
            size="tiny"
            styles={{ 'text-align': 'center', 'font-style': 'italic' }}
          >
            Cảm ơn quý khách hàng đã đồng hành cùng{' '}
            {bill?.supplier?.displayName}
          </Text>
        </>
      )}
    </Wrapper>
  );
};

export default K80BillContent;
